import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Star } from "lucide-react";

export default function Reviews() {
  const qc = useQueryClient();
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const [form, setForm] = useState({ name: "", city: "", rating: 5, comment: "" });
  const mut = useMutation({
    mutationFn: async (b) => (await api.post("/reviews", b)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reviews"] }); toast.success("Thank you for sharing your experience."); setForm({ name: "", city: "", rating: 5, comment: "" }); },
    onError: () => toast.error("Could not submit review."),
  });

  return (
    <div className="section-container py-16 sm:py-24">
      <div className="max-w-2xl">
        <div className="eyebrow mb-3">Guest Impressions</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Discretion. Precision. Presence.</h1>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reviews.map((r) => (
          <div key={r.id} className="lume-card p-6" data-testid={`review-${r.id}`}>
            <div className="flex gap-0.5 text-[color:var(--lume-gold)] mb-3">
              {Array.from({length: r.rating}).map((_,i)=><Star key={i} size={14} fill="currentColor"/>)}
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">"{r.comment}"</p>
            <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">— {r.name}{r.city ? ` · ${r.city}` : ""}</div>
          </div>
        ))}
      </div>

      <div className="mt-16 lume-card p-8 max-w-2xl">
        <div className="eyebrow mb-2">Share Your Experience</div>
        <h2 className="font-serif text-2xl text-white mb-6">Your voice matters.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Name</label>
            <input className="input-lume" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="review-name-input"/>
          </div>
          <div>
            <label className="field-label">City (optional)</label>
            <input className="input-lume" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} data-testid="review-city-input"/>
          </div>
        </div>
        <div className="mt-4">
          <label className="field-label">Rating</label>
          <div className="flex gap-2">
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setForm({...form, rating: n})} data-testid={`review-star-${n}`}
                className={`w-9 h-9 rounded-full border transition-colors ${n <= form.rating ? "border-[color:var(--lume-gold)] text-[color:var(--lume-gold)]" : "border-white/10 text-slate-500"}`}>
                <Star size={14} className="mx-auto" fill={n <= form.rating ? "currentColor" : "none"}/>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <label className="field-label">Your Experience</label>
          <textarea rows={4} className="input-lume" value={form.comment} onChange={(e)=>setForm({...form, comment:e.target.value})} data-testid="review-comment-input"/>
        </div>
        <button className="btn-gold mt-6" disabled={!form.name || !form.comment || mut.isPending} onClick={() => mut.mutate(form)} data-testid="review-submit-btn">
          {mut.isPending ? "Submitting…" : "Submit Review"}
        </button>
      </div>
    </div>
  );
}
